<template>
  <div class="offsite-page">

    <!-- ── Hero / Save the date ───────────────────────────────────────────── -->
    <section class="os-hero">
      <div class="os-hero__stage">
        <div class="os-hero__col">
          <!-- Palm-shadow panel with the "LA" knocked out of a black overlay -->
          <div class="os-hero__panel">
            <svg class="os-hero__la" viewBox="0 0 100 62" aria-hidden="true">
              <defs>
                <mask :id="laMaskId" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="62">
                  <rect x="0" y="0" width="100" height="62" fill="#fff" />
                  <!-- Baseline sits where the solid panel begins, exactly as in the
                       save-the-date — but the caps are shown whole rather than
                       cropped by the page edge. -->
                  <text
                    x="0"
                    y="62"
                    fill="#000"
                    font-family="'FH Oscar', Helvetica, Arial, sans-serif"
                    font-weight="700"
                    font-size="85"
                    letter-spacing="-16"
                  >LA</text>
                </mask>
              </defs>
              <rect x="0" y="0" width="100" height="62" fill="#000" :mask="`url(#${laMaskId})`" />
            </svg>

            <div class="os-hero__lockup">
              <p class="os-hero__brand">ORE<br />OFFSITE:<br />STUDIO</p>
              <p class="os-hero__at">At ASLA</p>
            </div>
          </div>

          <!-- Date block -->
          <div class="os-hero__date">
            <span class="os-hero__month">SEPT.</span>
            <span class="os-hero__days">16&ndash;17</span>
            <span class="os-hero__time">8 AM &ndash; 5 PM</span>
          </div>

          <!-- Lower panel -->
          <div class="os-hero__panel os-hero__panel--low" aria-hidden="true"></div>
        </div>

        <div class="os-hero__meta">
          <p>The Moxy + AC Hotel</p>
          <p>1260 South Figueroa Street</p>
          <p class="os-hero__url">ore.design/offsite</p>
        </div>

        <ul class="os-hero__rail" aria-hidden="true">
          <li v-for="word in railWords" :key="word">{{ word }}</li>
        </ul>
      </div>
    </section>

    <!-- ── Where / When + map ─────────────────────────────────────────────── -->
    <section class="os-where">
      <div class="os-where__map">
        <img src="@/assets/img/offsite/map-moxy-la.jpg"
             alt="Map showing the Moxy Downtown Los Angeles near the Los Angeles Convention Center" loading="lazy" />
      </div>
      <div class="os-where__detail">
        <dl>
          <dt>Where:</dt>
          <dd>
            The Moxy + AC Hotel<br />
            Moxy Studio &ndash; Mezzanine Level<br />
            Downtown Los Angeles<br />
            1260 South Figueroa Street
          </dd>
          <dt>When:</dt>
          <dd>
            September 16 &amp; 17, 2026<br />
            8:00 AM &ndash; 5:00 PM
          </dd>
        </dl>
      </div>
    </section>

    <!-- ── Event photography ──────────────────────────────────────────────── -->
    <section class="os-photos">
      <img src="@/assets/img/offsite/studio-1.jpg"
           alt="Attendees examining ORE fabricated elements at an ORE event" loading="lazy" />
      <img src="@/assets/img/offsite/studio-2.jpg"
           alt="An ORE team member walking guests through planters and fire features" loading="lazy" />
    </section>

    <!-- ── Introducing ────────────────────────────────────────────────────── -->
    <section class="os-intro">
      <div class="os-shell">
        <p class="os-eyebrow">Introducing</p>
        <h1 class="os-intro__title">The Next Evolution of ORE Collaboration</h1>
        <p class="os-intro__kicker">The natural progression of ORE OFFSITE into two new experiences.</p>
        <p class="os-intro__body">
          Years ago, we launched ORE OFFSITE to step away from the crowded Expo floor and create a
          space for real, personal connection. To elevate that connection even further, we are
          evolving the OFFSITE experience into two distinct paths:
        </p>
      </div>
    </section>

    <!-- ── The two paths ──────────────────────────────────────────────────── -->
    <section class="os-paths">
      <div class="os-shell os-shell--wide">
        <article v-for="path in paths" :key="path.name" class="os-path">
          <p class="os-path__name">{{ path.name }}</p>
          <p class="os-path__promise">{{ path.promise }}</p>
          <p class="os-path__sub">{{ path.sub }}</p>
          <p class="os-path__body">{{ path.body }}</p>
        </article>
      </div>
    </section>

    <!-- ── The ONSITE experience ──────────────────────────────────────────── -->
    <section class="os-onsite">
      <div class="os-shell">
        <h2 class="os-section-title">The ONSITE Experience</h2>
        <p class="os-onsite__lede">
          Rather than a standard facility tour, each immersion is a highly tailored partnership
          retreat. For the select design teams invited to attend, the experience includes:
        </p>
        <dl class="os-onsite__list">
          <div v-for="item in onsiteIncludes" :key="item.label" class="os-onsite__item">
            <dt>{{ item.label }}</dt>
            <dd>{{ item.copy }}</dd>
          </div>
        </dl>
      </div>
    </section>

    <!-- ── Testimonials ───────────────────────────────────────────────────── -->
    <section class="os-quotes">
      <div class="os-shell os-shell--wide">
        <figure v-for="quote in testimonials" :key="quote.attribution" class="os-quote">
          <blockquote>{{ quote.body }}</blockquote>
          <figcaption>{{ quote.attribution }}</figcaption>
        </figure>
      </div>
    </section>

    <!-- ── OFFSITE: STUDIO details ────────────────────────────────────────── -->
    <section class="os-studio">
      <div class="os-shell">
        <h2 class="os-section-title os-section-title--light">ORE OFFSITE: STUDIO</h2>
        <p class="os-studio__when">Sept. 16&ndash;17 &middot; 8 AM &ndash; 5 PM</p>

        <ul class="os-studio__features">
          <li v-for="feature in studioFeatures" :key="feature">{{ feature }}</li>
        </ul>

        <p class="os-studio__body">
          We invite you to step away from the crowds and come see us. If you have an active project
          pipeline, let&rsquo;s sit down, look at your plans, and solve complex site challenges
          together. Or, simply drop in to share a great cup of coffee, catch up, and talk about how
          we can host your firm for an upcoming ORE ONSITE event.
        </p>

      </div>
    </section>

    <!-- ── Capabilities ───────────────────────────────────────────────────── -->
    <section class="os-capabilities">
      <div class="os-shell">
        <p class="os-capabilities__title">Engineered to Match Your Vision.</p>
        <p>Bringing structural reality to the industry&rsquo;s most ambitious landscapes.</p>
        <p>Explore our capabilities &rarr; <a href="/">ore.design</a>.</p>
      </div>
    </section>

    <!-- ── The legacy of OFFSITE ──────────────────────────────────────────── -->
    <section class="os-legacy">
      <div class="os-shell">
        <p class="os-eyebrow">The Legacy of</p>
        <h2 class="os-legacy__title">OFFSITE</h2>
        <p>
          This evolution is rooted in a tradition we deeply cherish. Years ago, we made a conscious
          decision to step away from the chaotic ASLA Expo floor. We believed that real, personal
          connection required a different kind of space &mdash; a place where designers could slow
          down, discover something new, and enjoy being together.
        </p>
        <p>
          We gathered designers, artists, and friends in remarkable spaces, from the Museum of
          Contemporary Art in Los Angeles to the Barnes Foundation in Philadelphia. We shared
          evenings centered around inspiration, featuring visionary speakers like Cliff Garten,
          Janet Echelman, and Leyla Acaroglu.
        </p>
        <p>
          OFFSITE became much more than an event; it became a cherished tradition rooted entirely in
          relationship-building. That same desire for connection is exactly what drives the
          progression to ORE ONSITE and The ORE Studio today.
        </p>
      </div>
    </section>

    <!-- ── Previous OFFSITES ──────────────────────────────────────────────── -->
    <section class="os-previous">
      <div class="os-shell os-shell--wide">
        <h2 class="os-section-title os-section-title--light">Previous OFFSITES</h2>
        <div class="os-previous__grid">
          <router-link
            v-for="event in previousOffsites"
            :key="event.slug"
            :to="`/${event.slug}`"
            class="os-previous__card"
          >
            <img :src="event.cardImage" :alt="`ORE OFFSITE ${event.year} — ${event.city}`" loading="lazy" />
            <span class="os-previous__city">{{ event.city }}</span>
            <span class="os-previous__year">{{ event.year }}</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ── Sign off ───────────────────────────────────────────────────────── -->
    <section class="os-signoff">
      <div class="os-shell">
        <p>Thank you for being such an integral part of the ORE journey.</p>
        <p>We look forward to seeing you in September and welcoming you to our headquarters soon!</p>
        <p class="os-signoff__name">Your friends in design and development,<br /><strong>ORE</strong></p>
      </div>
    </section>

  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue'
import { offsiteYears } from '@/data/offsiteYears.js'

// Unique id so the SVG mask never collides with another instance on the page.
const laMaskId = 'offsite-la-mask'

const railWords = ['Relax', 'Chat', 'Good Food', 'Great Showcases']

const paths = [
  {
    name: 'ORE ONSITE',
    promise: 'Bringing you into our world.',
    sub: 'An invite-only, 3-day immersion',
    body: 'For years, we brought the ORE experience to you. Moving forward, we are curating invite-only, 3-day immersions to bring our core design partners directly to our headquarters in Salt Lake City, Utah.',
  },
  {
    name: 'OFFSITE: STUDIO',
    promise: 'Meeting you where you are.',
    sub: 'A private, curated space near the ASLA convention center',
    body: 'To elevate the focused, high-value conversations we cherish, our approach to the ASLA Conference is progressing. This year, we are trading the large evening event for The ORE Studio — a private space close to the convention center, designed specifically to reflect the ORE vibe away from the chaos of the trade show floor.',
  },
]

const onsiteIncludes = [
  {
    label: 'Behind-the-Scenes Access:',
    copy: 'An uncompromised, deep dive into our engineering, prototyping, and fabrication floors to see how bold design intent becomes structural reality.',
  },
  {
    label: 'Curated Hospitality:',
    copy: 'Engaging with the natural environment that inspires us — from exploring the summer landscapes to winter sports in the Wasatch Mountains.',
  },
  {
    label: 'Exclusive Networking:',
    copy: 'Direct introductions to our specialized, collaborative partners, with premium VIP hospitality thoughtfully designed around your team.',
  },
]

const testimonials = [
  {
    body: 'We are so incredibly grateful to have partners in design like ORE that share the same quality and experience goals in our custom designs. I feel like my mind filled with so many awesome ideas to push myself and team to take it to the next level.',
    attribution: 'Colleen B., Mariani Landscaping',
  },
  {
    body: 'I love to visit companies with passion, creativity and drive to push and test their product in strides for perfection. What was clear to me was how engaged your team is in their contribution to the mission.',
    attribution: 'Brian C., Hoffman Landscaping',
  },
]

const studioFeatures = [
  'Unhurried & intimate environment',
  'By the convention',
  'Drinks, pastries, & lunch',
  'Enriching design discussion',
  'Tactile product showcases',
]

// Newest first, so 2025 leads the grid.
const previousOffsites = [...offsiteYears].reverse()

onMounted(() => document.body.classList.add('page-offsite'))
onBeforeUnmount(() => document.body.classList.remove('page-offsite'))
</script>

<style lang="scss" scoped>
$black: #000;
$white: #fff;
$orange: #ff4c00;
$green-dark: #001610;
$mist: #ebf8ef;
$font: 'FH Oscar', Helvetica, Arial, sans-serif;

.offsite-page {
  background: $black;
  color: $white;
  font-family: $font;
}

.os-shell {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;

  &--wide { max-width: 1100px; }
}

.os-eyebrow {
  font-weight: 500;
  font-size: 0.8125rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: $orange;
  margin-bottom: 14px;
}

.os-section-title {
  font-weight: 600;
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  line-height: 1.15;
  margin-bottom: 24px;
  color: $white;

  &--light { font-weight: 300; }
}

// ── Hero ─────────────────────────────────────────────────────────────────────
// Every dimension below is a ratio taken off the InDesign save-the-date, expressed
// against --p (the light panel's width). --s is the stage width. Measured values:
// panel = 49.4% of stage, panel1 h = 1.187p, date type spans the full panel width.
.os-hero {
  --s: min(1100px, 100vw);
  --p: calc(var(--s) * 0.494);
  background: $black;
  padding: calc(var(--s) * 0.06) 0 calc(var(--s) * 0.05);
}

.os-hero__stage {
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  padding-left: calc(var(--s) * 0.079);
  padding-right: calc(var(--s) * 0.091);
}

.os-hero__col { width: var(--p); }

.os-hero__panel {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1.807;   // 0.62 LA band + 1.187 solid panel
  background-image: url('@/assets/img/offsite/palm-shadow.jpg');
  background-size: cover;
  background-position: 18% 40%;

  &--low {
    aspect-ratio: 1 / 0.414;
    margin-top: calc(var(--p) * 0.028);
    background-position: 40% 70%;
  }
}

// Black plate with the LA knocked out of it, so the panel shows through the letters.
.os-hero__la {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(var(--p) * 0.62);    // full cap height of the LA
  display: block;
}

.os-hero__lockup {
  position: absolute;
  left: calc(var(--p) * 0.104);
  top: calc(var(--p) * 1.135);      // 0.62 LA band + 0.434 of the solid panel
  color: $black;
}

.os-hero__brand {
  color: $black;
  font-weight: 700;
  font-size: calc(var(--p) * 0.085);
  line-height: 0.98;
  letter-spacing: -0.01em;
}

.os-hero__at {
  color: $black;
  font-weight: 300;
  font-size: calc(var(--p) * 0.060);
  line-height: 1.2;
  letter-spacing: 0.01em;
  margin-top: calc(var(--p) * 0.012);
}

.os-hero__date {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: calc(var(--p) * 0.058);
  font-weight: 500;
  letter-spacing: -0.01em;

  // Brushed-silver treatment from the mock.
  background: linear-gradient(
    170deg,
    #ffffff 0%,
    #d6d6d6 32%,
    #8a8a8a 52%,
    #efefef 72%,
    #b4b4b4 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.os-hero__month { font-size: calc(var(--p) * 0.410); line-height: 0.79; }
.os-hero__days  { font-size: calc(var(--p) * 0.410); line-height: 0.75; }
.os-hero__time  { font-size: calc(var(--p) * 0.186); line-height: 1; }

.os-hero__meta {
  position: absolute;
  right: calc(var(--s) * 0.091);
  bottom: calc(var(--p) * 0.067);
  text-align: right;
  font-weight: 500;
  font-size: calc(var(--p) * 0.047);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.88);

  p { color: inherit; }

  .os-hero__url {
    font-weight: 700;
    color: $white;
  }
}

.os-hero__rail {
  position: absolute;
  top: 0;
  right: calc(var(--s) * 0.021);
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  font-size: calc(var(--s) * 0.036);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $white;

  li { writing-mode: vertical-rl; }
}

// Below the rail breakpoint the column goes full width and the meta returns to flow.
@media (max-width: 700px) {
  .os-hero {
    --s: 100vw;
    --p: calc(var(--s) - 48px);
  }
  .os-hero__stage { padding-left: 24px; padding-right: 24px; }
  .os-hero__rail { display: none; }
  .os-hero__meta {
    position: static;
    margin-top: calc(var(--p) * 0.06);
    font-size: calc(var(--p) * 0.040);
  }
}

// ── Where / When ─────────────────────────────────────────────────────────────
.os-where {
  display: grid;
  grid-template-columns: 58.3% 41.7%;   // map / detail split from the email layout
  background: $black;
  max-width: 1100px;                    // scales with the rest of the page, not the window
  margin: 0 auto;

  @media (max-width: 700px) { grid-template-columns: 1fr; }
}

.os-where__map {
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 1.427;
  }
}

.os-where__detail {
  display: flex;
  align-items: center;
  padding: clamp(24px, 4vw, 48px);

  dt {
    font-weight: 700;
    font-size: clamp(1rem, 1.6vw, 1.25rem);
    color: $white;
    margin-bottom: 4px;
  }

  dd {
    margin: 0 0 clamp(16px, 2.4vw, 28px);
    font-weight: 300;
    font-size: clamp(0.9375rem, 1.5vw, 1.1875rem);
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.92);

    &:last-child { margin-bottom: 0; }
  }
}

// ── Event photography ────────────────────────────────────────────────────────
.os-photos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: $black;
  max-width: 1100px;
  margin: 0 auto;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1.5;   // native 3:2 of the supplied photography
    object-fit: cover;
  }

  @media (max-width: 700px) { grid-template-columns: 1fr; }
}

// ── Introducing ──────────────────────────────────────────────────────────────
.os-intro {
  padding: 96px 0 72px;
}

.os-intro__title {
  color: $white;
  font-weight: 600;
  font-size: clamp(2rem, 5vw, 3.25rem);
  line-height: 1.1;
  margin-bottom: 20px;
}

.os-intro__kicker {
  font-weight: 300;
  font-size: clamp(1.125rem, 2.2vw, 1.5rem);
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 28px;
}

.os-intro__body {
  font-size: 1.0625rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.85);
}

// ── Two paths ────────────────────────────────────────────────────────────────
.os-paths {
  padding: 24px 0 96px;

  .os-shell {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 48px;

    @media (max-width: 800px) { grid-template-columns: 1fr; }
  }
}

.os-path {
  border-top: 1px solid rgba(255, 255, 255, 0.22);
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.os-path__name {
  color: $white;
  font-weight: 700;
  font-size: clamp(1.5rem, 3vw, 2rem);
  letter-spacing: 0.01em;
  margin-bottom: 10px;
}

.os-path__promise {
  font-weight: 300;
  font-size: 1.25rem;
  color: $orange;
  margin-bottom: 6px;
}

.os-path__sub {
  font-size: 0.9375rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 18px;
}

.os-path__body {
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
}

// ── ONSITE experience ────────────────────────────────────────────────────────
.os-onsite {
  background: $green-dark;
  padding: 88px 0;

  .os-shell { display: flex; flex-direction: column; align-items: flex-start; }
}

.os-onsite__lede {
  font-size: 1.0625rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 36px;
}

.os-onsite__list {
  display: grid;
  gap: 26px;
  width: 100%;
}

.os-onsite__item {
  dt {
    font-weight: 600;
    font-size: 1.0625rem;
    color: $mist;
    margin-bottom: 6px;
  }

  dd {
    margin: 0;
    font-size: 1rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.78);
  }
}

// ── Testimonials ─────────────────────────────────────────────────────────────
.os-quotes {
  padding: 88px 0;

  .os-shell {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 48px;

    @media (max-width: 800px) { grid-template-columns: 1fr; }
  }
}

.os-quote {
  margin: 0;

  blockquote {
    margin: 0 0 16px;
    font-weight: 300;
    font-style: italic;
    font-size: 1.0625rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.9);
  }

  figcaption {
    font-weight: 600;
    font-size: 0.875rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $orange;
  }
}

// ── STUDIO details ───────────────────────────────────────────────────────────
.os-studio {
  background: $mist;
  color: $green-dark;
  padding: 88px 0;

  .os-shell { display: flex; flex-direction: column; align-items: flex-start; }
  .os-section-title { color: $green-dark; }
}

.os-studio__when {
  color: $green-dark;
  font-weight: 600;
  font-size: clamp(1.125rem, 2.4vw, 1.5rem);
  letter-spacing: 0.04em;
  margin-bottom: 28px;
}

.os-studio__features {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 32px;

  li {
    border: 1px solid rgba(0, 22, 16, 0.25);
    border-radius: 100px;
    padding: 8px 18px;
    font-size: 0.8125rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
}

.os-studio__body {
  color: $green-dark;
  font-size: 1.0625rem;
  line-height: 1.75;
  margin-bottom: 28px;
}


// ── Capabilities ─────────────────────────────────────────────────────────────
.os-capabilities {
  background: $white;
  padding: clamp(40px, 6vw, 72px) 0;

  .os-shell {
    border-top: 2px solid $green-dark;
    border-bottom: 2px solid $green-dark;
    padding-top: clamp(28px, 4vw, 48px);
    padding-bottom: clamp(28px, 4vw, 48px);
    text-align: center;
  }

  p {
    color: $green-dark;
    font-size: clamp(1rem, 1.9vw, 1.3125rem);
    line-height: 1.55;
  }

  a { color: $green-dark; text-decoration: underline; }
}

.os-capabilities__title {
  font-weight: 700;
  margin-bottom: 4px;
}

// ── Legacy ───────────────────────────────────────────────────────────────────
.os-legacy {
  padding: 88px 0;

  // :not() keeps the orange .os-eyebrow from being caught by this body-copy rule.
  p:not(.os-eyebrow) {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.85);

    & + p { margin-top: 20px; }
  }
}

.os-legacy__title {
  color: $white;
  font-weight: 700;
  font-size: clamp(2.5rem, 8vw, 5rem);
  line-height: 1;
  margin-bottom: 28px;
}

// ── Previous OFFSITES ────────────────────────────────────────────────────────
.os-previous {
  padding: 0 0 88px;

  .os-section-title { text-align: center; }
}

.os-previous__grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-auto-flow: row;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 520px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.os-previous__card {
  display: block;
  text-decoration: none;
  color: $white;
  transition: transform 0.3s;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 10px;
    filter: grayscale(20%);
    transition: filter 0.3s;
    margin-bottom: 10px;
  }

  .os-previous__city {
    display: block;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.3;
  }

  .os-previous__year {
    display: block;
    font-weight: 300;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.65);
  }

  &:hover {
    transform: translateY(-4px);
    img { filter: grayscale(0%); }
  }
}

// ── Sign off ─────────────────────────────────────────────────────────────────
.os-signoff {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding: 72px 0 96px;

  p {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.85);

    & + p { margin-top: 16px; }
  }
}

.os-signoff__name {
  margin-top: 28px !important;
  color: $white !important;

  strong { font-weight: 700; letter-spacing: 0.08em; }
}
</style>

<style lang="scss">
.page-offsite .main-footer {
  display: none;
}

// Nav sits over the black hero until the header picks up its scrolled state.
.page-offsite .main-header:not(.is-scrolled) {
  .main-header__nav-list a {
    color: #fff !important;
  }
  .main-header__logo svg path,
  .main-header__logo svg rect,
  .main-header__logo svg polygon {
    fill: #fff !important;
  }
}
</style>
